/**
 * Production clock adapter — clock.local, provider 'local', tier free.
 *
 * This is a CLIENT-ONLY adapter: the clock is a no-fetch tile whose data is
 * produced locally in the browser (use-tile-view-model.ts builds ClockData on
 * a 1-second interval). The registry still needs an entry so the renderer can
 * call resolvedAdapter.glanceView(clockData, glanceMap) — this is that entry.
 *
 * CLIENT / SERVER SPLIT (ADR-002 §5):
 *   All members are CLIENT-SIDE. There are no server-only members because
 *   clock.local never makes an upstream request.
 *
 *   server-only stubs: buildRequest / responseSchema / normalize are omitted —
 *   ClientAdapter<Params, Data> is a Pick of the full ProviderAdapter that
 *   excludes those three, matching the exact shape used in the registry.
 *
 * Immutability: the exported object is frozen; glanceView returns a new object.
 */
import { z } from 'zod';
import {
  clockParamsSchema,
  deriveCacheKey,
  type ClockParams,
} from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { ClientAdapter, GlanceViewModel } from 'briefpane/contracts';
import type { ClockData } from './types.js';

/** Zod shape for ClockData — mirrors the fields ClockData declares. */
export const clockDataShape = z.object({
  time: z.string(),
  date: z.string(),
  weekday: z.string(),
  tz: z.string(),
});

/**
 * Map ClockData + GlanceMap → GlanceViewModel.
 *
 * glance-grammar-spec §1.3 (Clock row):
 *   primary : "9:41"             (time string, ≤ 8 chars)
 *   delta   : "Thursday, Jul 24" (weekday + date, ≤ 20 chars)
 *   state   : neutral
 *
 * Pure. Returns a new object every call.
 */
export function clockGlanceView(data: ClockData, _glance: GlanceMap): GlanceViewModel {
  const primary = data.time.slice(0, 8);
  const delta = `${data.weekday}, ${data.date}`.slice(0, 20);
  return {
    dataState: 'ok',
    glanceState: 'neutral',
    slots: { primary, delta },
    label: null,
    fetchedAt: Date.now(),
  };
}

/**
 * The production clock adapter.
 *
 * Satisfies ClientAdapter<ClockParams, ClockData> — the subset of ProviderAdapter
 * that ships to the client bundle. Server-only members (buildRequest, responseSchema,
 * normalize) are absent because clock.local never makes an upstream request.
 */
export const clockAdapter: ClientAdapter<ClockParams, ClockData> = Object.freeze({
  type: 'clock.local' as const,
  provider: 'local' as const,
  tier: 'free' as const,
  ttlSec: 0,
  paramsSchema: clockParamsSchema,
  dataShape: clockDataShape,

  cacheKey: (params: ClockParams): string =>
    deriveCacheKey({ type: 'clock.local', provider: 'local', params }),

  glanceView: clockGlanceView,
});
